import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import {
  canReorderConsultation,
  ConsultationStatus,
  formatConsultationDuration,
  type TranscriptorConsultation,
  TranscriptorDetailsPopover,
  transcriptorMessages,
} from '@/entities/transcriptor';

import { type RecordingRowActions } from '../../config';

import styles from './RecordingRow.module.scss';

type RecordingRowProps = {
  consultation: TranscriptorConsultation;
  userTimezone: string;
  isStale: boolean;
  hideMode: boolean;
  className?: string;
} & RecordingRowActions;

export const RecordingRow: FC<RecordingRowProps> = ({
  consultation,
  userTimezone,
  isStale,
  hideMode,
  className,
  onOpen,
  onForceStop,
  onArchive,
  onDelete,
  onReorder,
  onRestore,
}) => {
  const { formatDate } = useIntl();
  const { isMobile } = useMedia();
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    id,
    status,
    isArchived,
    canArchive,
    canRestore,
    canDelete,
    name,
    schemaName,
    date,
    audioDuration,
  } = consultation;

  const isClickable = status !== 'created';
  const time = formatDate(date, {
    timeStyle: 'short',
    timeZone: userTimezone,
  });
  const meta = [
    isMobile ? time : null,
    schemaName,
    formatConsultationDuration(audioDuration),
  ]
    .filter(Boolean)
    .join(' · ');

  const abstractText = consultation.abstract?.content?.trim();
  const showAbstract = status === 'succeeded' && Boolean(abstractText);

  const removeWith = (remove: NonNullable<typeof onDelete>) => async () => {
    const confirmed = await remove(id, {
      onError: () => setIsRemoving(false),
    });

    if (confirmed) {
      setIsRemoving(true);
    }
  };

  const handleReorder = () =>
    onReorder ? onReorder(id) : Promise.resolve(false);

  const handleRestore = async () => {
    if (!onRestore) {
      return;
    }

    setIsRemoving(true);

    const restored = await onRestore(id);

    if (!restored) {
      setIsRemoving(false);
    }
  };

  // As with reports: with the archive on, a live recording is archived and
  // only an archived one is deleted; with it off, deleting is the only way out.
  const archiveHandler =
    hideMode && onArchive && canArchive && !isArchived
      ? removeWith(onArchive)
      : undefined;
  const deleteHandler =
    onDelete && canDelete && (isArchived || !hideMode)
      ? removeWith(onDelete)
      : undefined;
  const reorderHandler =
    onReorder && canReorderConsultation(status) && !isArchived
      ? handleReorder
      : undefined;
  const restoreHandler =
    onRestore && canRestore && isArchived ? handleRestore : undefined;
  const hasAvailableActions = Boolean(
    archiveHandler || deleteHandler || reorderHandler || restoreHandler,
  );

  const handleClick = () => {
    if (isClickable) {
      onOpen(consultation);
    }
  };

  return (
    <div
      data-component="recording-row"
      className={cn(
        styles.container,
        !isClickable && styles.notClickable,
        isRemoving && styles.removing,
        isArchived && styles.hidden,
        className,
      )}
      onClick={handleClick}
    >
      {!isMobile && <span className={cn('p2', styles.time)}>{time}</span>}

      <Icon name="scribe" size={28} className={styles.icon} />

      <div className={styles.content}>
        <h4 className={cn('h5', styles.title)}>
          {name ?? schemaName ?? (
            <FormattedMessage
              {...transcriptorMessages.consultationTypeDental}
            />
          )}
        </h4>

        {meta && <p className={cn('p2', styles.meta)}>{meta}</p>}

        {showAbstract ? (
          <p className={cn('p2', styles.abstract)}>{abstractText}</p>
        ) : (
          <ConsultationStatus
            consultation={consultation}
            isStale={isStale}
            onForceStop={onForceStop}
            className={styles.status}
          />
        )}
      </div>

      {hasAvailableActions && (
        <TranscriptorDetailsPopover
          className={styles.actions}
          onArchive={archiveHandler}
          onDelete={deleteHandler}
          onReorder={reorderHandler}
          onRestore={restoreHandler}
        />
      )}
    </div>
  );
};
